import { useEffect } from 'react'
import { useLocation, matchPath } from 'react-router-dom'
import Parse from 'parse'
import Config from './config'

// Generic title shown before login and after logout. Nothing about the
// current page is ever exposed here so no admin info leaks to signed-out
// users (or anyone reading the browser tab / history on the login screen).
const BASE_TITLE = 'MySwimPro Admin'

// Maps routes to human-readable tab labels. Detail routes carry an `:id`
// param so admins can tell multiple open records apart at a glance. Each
// entry is matched with `exact: true`, so list and detail paths never
// collide and ordering does not matter.
const TITLE_ROUTES = [
  // Detail routes (append the record id).
  { path: '/workouts/:id', label: 'Workout' },
  { path: '/adminworkouts/:id', label: 'Admin Workout' },
  { path: '/challenges/:id', label: 'Challenge' },
  { path: '/videos/:id', label: 'Video' },
  { path: '/tipofthedays/:id', label: 'Tip of the Day' },
  { path: '/exercises/:id', label: 'Dryland Exercise' },
  { path: '/equipment/:id', label: 'Equipment' },
  { path: '/effortVariation/:id', label: 'Effort Variation' },
  { path: '/skilllevels/:id', label: 'Skill Level' },
  { path: '/wod/:id', label: 'WOD' },
  { path: '/trainingplans/:id', label: 'Member Plan' },
  { path: '/adminplans/:id', label: 'Admin Plan' },
  { path: '/achievements/:id', label: 'Achievement' },
  { path: '/userInfo/:id', label: 'User Info' },
  { path: '/members/:id', label: 'Member' },
  { path: '/completedWorkouts/:id', label: 'Completed Workout' },
  { path: '/experiments/:id', label: 'Experiment' },
  { path: '/swimCoachConversations/:id', label: 'Coach Conversation' },
  // List / tool routes.
  { path: '/members', label: 'Members' },
  { path: '/emails', label: 'Download Email List' },
  { path: '/passwords', label: 'Password Reset' },
  { path: '/swimreport', label: 'Swim Report' },
  { path: '/userInfo', label: 'User Info' },
  { path: '/subscription', label: 'Subscriptions' },
  { path: '/wods', label: 'WODs' },
  { path: '/adminworkouts', label: 'Admin Workouts' },
  { path: '/workouts', label: 'Member Workouts' },
  { path: '/exercises', label: 'Dryland Exercises' },
  { path: '/equipment', label: 'Equipment' },
  { path: '/effortvariation', label: 'Effort Variation' },
  { path: '/skilllevels', label: 'Skill Levels' },
  { path: '/genWorkout', label: 'Workout Generator' },
  { path: '/adminplans', label: 'Admin Plans' },
  { path: '/trainingplans', label: 'Member Plans' },
  { path: '/genPlan', label: 'Plan Generator' },
  { path: '/videos', label: 'Video Library' },
  { path: '/tipofthedays', label: 'Tip of the Day' },
  { path: '/featuredvideos', label: 'Featured Videos' },
  { path: '/completedWorkouts', label: 'Completed Workouts' },
  { path: '/swimLocations', label: 'Swim Locations' },
  { path: '/fitfiledebug', label: 'Fit File Debugger' },
  { path: '/watchSessionDebug', label: 'Apple Watch Session Debugger' },
  { path: '/challenges', label: 'Challenges' },
  { path: '/achievements', label: 'Achievements' },
  { path: '/swimCoachConversations', label: 'Coach Conversations' },
  { path: '/coachFeedback', label: 'Coach Feedback' },
  { path: '/coachMemories', label: 'Coach Memories' },
  { path: '/webtools', label: 'Web Tools' },
  { path: '/downloadtool', label: 'Download Link Tool' },
  { path: '/experiments', label: 'Experiments' },
  { path: '/featuredcontent', label: 'Feed' }
]

// Returns the label for a pathname, appending the record id for detail
// routes, or null when no route matches (e.g. the home page).
function pageLabel (pathname) {
  for (const route of TITLE_ROUTES) {
    const match = matchPath(pathname, { path: route.path, exact: true })
    if (match) {
      const id = match.params && match.params.id
      return id ? `${route.label} ${id}` : route.label
    }
  }
  return null
}

// Builds the document title for a pathname. Page-level detail is only
// included when a user is signed in; otherwise the generic base title is
// returned so nothing leaks to signed-out users.
export function computeTitle (pathname) {
  if (Parse.User.current() == null) {
    return BASE_TITLE
  }
  const envSuffix = Config.getEnvironment().toLowerCase() === 'dev' ? ' 🧪 dev' : ''
  const label = pageLabel(pathname)
  return label
    ? `${label} · ${BASE_TITLE}${envSuffix}`
    : `${BASE_TITLE}${envSuffix}`
}

// Renders nothing; keeps document.title in sync with the current route and
// auth state. Mount once inside the router.
export default function PageTitle () {
  const location = useLocation()
  const currentUser = Parse.User.current()
  const userId = currentUser ? currentUser.id : null

  useEffect(() => {
    document.title = computeTitle(location.pathname)
  }, [location.pathname, userId])

  return null
}
